import { useState, useRef } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function getCroppedBlob(imageSrc, cropPixels) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  const scale = Math.min(1, 1200 / cropPixels.width);
  canvas.width = Math.round(cropPixels.width * scale);
  canvas.height = Math.round(cropPixels.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, canvas.width, canvas.height);
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
}

export const ImageUpload = ({ aspect = 1, label, onUploaded, testId = "image-upload" }) => {
  const { t } = useLang();
  const inputRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("select_img_err")); return; }
    const reader = new FileReader();
    reader.onload = () => { setSrc(reader.result); setCrop({ x: 0, y: 0 }); setZoom(1); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const upload = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, pixels);
      const fd = new FormData();
      fd.append("file", blob, "upload.jpg");
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(`${BACKEND_URL}${res.data.url}`);
      toast.success(t("photo_uploaded"));
      setSrc(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("upload_fail"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" className="rounded-full" onClick={() => inputRef.current?.click()} data-testid={`${testId}-btn`}>
        <Camera size={15} className="mr-1.5" /> {label || t("upload_photo")}
      </Button>
      <Dialog open={!!src} onOpenChange={(o) => !o && setSrc(null)}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{t("crop_title")}</DialogTitle></DialogHeader>
          <div className="relative w-full h-72 bg-secondary rounded-xl overflow-hidden">
            {src && (
              <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect}
                onCropChange={setCrop} onZoomChange={setZoom}
                onCropComplete={(_, px) => setPixels(px)} />
            )}
          </div>
          <div className="flex items-center gap-3 px-1">
            <span className="text-xs text-muted-foreground">{t("zoom_lbl")}</span>
            <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v) => setZoom(v[0])} className="flex-1" data-testid={`${testId}-zoom`} />
          </div>
          <DialogFooter>
            <Button onClick={upload} disabled={busy} className="rounded-full w-full h-11 font-semibold" data-testid={`${testId}-confirm`}>
              {busy ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> {t("uploading")}</> : t("crop_upload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
